/* eslint-disable */
/**
 * 返回滚动父级元素，如果是window则返回null
 * @param {Element} element dom element
 * @return {Element} scroll parent
 */
export function getScrollParent(element) {
    if (!element) {
        return;
    }

    do {
        const nodeName = element.nodeName.toLowerCase();
        if (nodeName === 'body' || nodeName === 'html') {
            return;
        }

        const { overflow, overflowX, overflowY } = getComputedStyle(element);
        if (/(auto|scroll)/.test(overflow + overflowY + overflowX)) {
            return element;
        }

        element = element.parentNode;
    } while (element);
}

/**
 * 返回position为absolute,relative,fixed的父级元素，如果是window则返回null
 * @param {Element} element dom element
 * @return {Element} positioned parent
 */
export function getPositionedParent(element) {
    if (!element) {
        return;
    }
    while ((element = element.parentNode)) {
        const nodeName = element.nodeName.toLowerCase();
        if (nodeName === 'body' || nodeName === 'html') {
            return;
        }
        const { position } = getComputedStyle(element);
        if (/(absolute|relative|fixed)/.test(position)) {
            return element;
        }
    }
}

/**
 * 返回offset parent
 * @param {Element} element original element.
 * @return {Object} offset object {top: xxx, left: xxx}
 */
export function offset(element) {
    const rect = element.getBoundingClientRect();
    const win = getWindow(element);
    const scrollLeft = getScrollLeft(win);
    const scrollTop = getScrollTop(win);
    return { top: rect.top + scrollTop, left: rect.left + scrollLeft };
}

/**
 * 返回offset parent
 * @param {Element} element original element.
 * @return {Element} offset parent
 */
export function getOffsetParent(element) {
    const { offsetParent } = element;
    const nodeName = offsetParent && offsetParent.nodeName.toLowerCase();

    if (!nodeName || nodeName === 'body' || nodeName === 'html') {
        return element.ownerDocument.documentElement;
    }

    return offsetParent;
}

/**
 * 得到浏览器计算后的css样式
 * @param {Element} element element
 * @param {string=} property css property
 * @return {string|Object} css style value.
 */
export function getComputedStyle(element, property) {
    if (element.nodeType !== 1) {
        return [];
    }

    const css = window.getComputedStyle(element, null);
    return property
        ? // https://github.com/jquery/jquery/blob/473d2ea7d5e4ef0a5b8e0305197f483c136ee4ab/src/css/curCSS.js#L21
    // getPropertyValue is needed for:
    //   util.getComputedStyle(element, 'filter') (IE 9 only, #12537)
    //   util.getComputedStyle(element, '--customProperty) (#3144)
        css.getPropertyValue(property) || css[property]
        : css;
}

/**
 * 获取元素的大小位置
 * @param {Element|string} element dom element or 'html'.
 * @return {Object} rect value
 */
export function getRect(element) {
    let rect;
    if (element === 'viewport') {
        const scrollTop = getScrollTop();
        const scrollLeft = getScrollLeft();
        const viewWidth = getViewWidth();
        const viewHeight = getViewHeight();
        return {
            top: scrollTop,
            left: scrollLeft,
            right: scrollLeft + viewWidth,
            bottom: scrollTop + viewHeight,
            width: viewWidth,
            height: viewHeight
        };
    }

    if (element === 'html' || element.nodeName === 'HTML') {
        const pageWidth = getPageWidth();
        const pageHeight = getPageHeight();

        // viewport
        rect = {
            top: 0,
            left: 0,
            right: pageWidth,
            bottom: pageHeight,
            width: pageWidth,
            height: pageHeight
        };
        return rect;
    }

    const isIE10 = window.navigator.appVersion.indexOf('MSIE 10') !== -1;
    if (isIE10) {
        // IE10 10 FIX: Please, don't ask, the element isn't
        // considered in DOM in some circumstances...
        // This isn't reproducible in IE10 compatibility mode of IE11
        try {
            rect = element.getBoundingClientRect();
        } catch (err) {
            rect = {};
        }
    } else {
        rect = element.getBoundingClientRect();
    }
    const win = getWindow(element);
    const scrollTop = getScrollTop(win);
    const scrollLeft = getScrollLeft(win);
    const left = (rect.left || 0) + scrollLeft;
    const top = (rect.top || 0) + scrollTop;
    const width = rect.width || 0;
    const height = rect.height || 0;
    const right = left + width;
    const bottom = top + height;
    return {
        left,
        top,
        right,
        bottom,
        width,
        height
    };
}

/**
 * get width and height of an element element, which including margin.
 * @param {Element} element dom element.
 * @return {Object} rect value
 */
export function getWidthAndHeight(element) {
    const rect = {
        width: element.offsetWidth,
        height: element.offsetHeight
    };
    const styles = getComputedStyle(element);
    rect.width += parseFloat(styles.marginLeft) + parseFloat(styles.marginRight);
    rect.height += parseFloat(styles.marginTop) + parseFloat(styles.marginBottom);
    return rect;
}

/**
 * get window scroll top.
 * @param {Window=} win window.
 * @return {number} window scroll top
 */
export function getScrollTop(win) {
    win = win || window;
    const doc = win.document;
    return win.pageYOffset || doc.documentElement.scrollTop || doc.body.scrollTop;
}

/**
 * get window scroll left.
 * @param {Window=} win window.
 * @return {number} window scroll left
 */
export function getScrollLeft(win) {
    win = win || window;
    const doc = win.document;
    return win.pageXOffset || doc.documentElement.scrollLeft || doc.body.scrollLeft;
}

/**
 * get webpage height.
 * @param {Window=} win window.
 * @return {number} webpage height
 */
export function getPageHeight(win) {
    win = win || window;
    const doc = win.document;
    const { body } = doc;
    const de = doc.documentElement;
    const client = doc.compatMode === 'BackCompat' ? body : doc.documentElement;

    return Math.max(de.scrollHeight, body.scrollHeight, client.clientHeight);
}

/**
 * get webpage width.
 * @param {Window=} win window.
 * @return {number} webpage width
 */
export function getPageWidth(win) {
    win = win || window;
    const doc = win.document;
    const { body } = doc;
    const de = doc.documentElement;
    const client = doc.compatMode === 'BackCompat' ? body : doc.documentElement;

    return Math.max(de.scrollWidth, body.scrollWidth, client.clientWidth);
}

/**
 * get viewport height.
 * @param {Window=} win window.
 * @return {number} webpage height
 */
export function getViewHeight(win) {
    win = win || window;
    const doc = win.document;
    const client = doc.compatMode === 'BackCompat' ? doc.body : doc.documentElement;
    return client.clientHeight;
}

/**
 * get viewport width.
 * @param {Window=} win window.
 * @return {number} webpage width
 */
export function getViewWidth(win) {
    win = win || window;
    const doc = win.document;
    const client = doc.compatMode === 'BackCompat' ? doc.body : doc.documentElement;
    return client.clientWidth;
}

/**
 * is outside or not
 * @param {Object} inside rect.
 * @param {Object} container rect.
 * @param {number=} threshould threshould value.
 * @return {boolean} is outside or not.
 */
export function isOutside(inside, container, threshould) {
    threshould = threshould || 0;
    const top = inside.top - container.top - threshould;
    const left = inside.left - container.left - threshould;
    const right = container.right - inside.right - threshould;
    const bottom = container.bottom - inside.bottom - threshould;
    return top <= 0 || left <= 0 || right <= 0 || bottom <= 0;
    /*
    if ((top >= 0) && (left >= 0) && (right >= 0) && (bottom >= 0)) {
        return;
    }

    const values = {
        top,
        left,
        right,
        bottom
    };
    const directions = ['top', 'left', 'right', 'bottom'];
    directions.sort((d1, d2) => values[d1] - values[d2]);
    return directions;
    */
}

/**
 * Add classname on specified elememnt.
 * @param {Element} element dom element.
 * @param {string} clazz class name.
 * Add class with compatibility for SVG since classList is not supported on SVG elements in IE
 */
export function addClass(element, clazz) {
    if (!clazz || !(clazz = clazz.trim()) || hasClass(element, clazz)) {
        return;
    }

    if (element.classList) {
        if (clazz.indexOf(' ') > -1) {
            clazz.split(/\s+/).forEach(c => element.classList.add(c));
        } else {
            element.classList.add(clazz);
        }
    } else {
        const cur = ` ${element.getAttribute('class') || ''} `;
        element.setAttribute('class', (cur + clazz).trim());
    }
}

/**
 * Remove classname on specified elememnt.
 * @param {Element} element dom element.
 * @param {string} clazz class name.
 * Remove class with compatibility for SVG since classList is not supported on SVG elements in IE
 * Refs to https://github.com/vuejs/vue/blob/dev/src/platforms/web/runtime/class-util.js
 */
export function removeClass(element, clazz) {
    if (!clazz || !(clazz = clazz.trim())) {
        return;
    }
    if (element.classList) {
        if (clazz.indexOf(' ') > -1) {
            clazz.split(/\s+/).forEach(c => element.classList.remove(c));
        } else {
            element.classList.remove(clazz);
        }
        if (!element.classList.length) {
            element.removeAttribute('class');
        }
    } else {
        let cur = ` ${element.getAttribute('class') || ''} `;
        const tar = ` ${clazz} `;
        while (cur.indexOf(tar) >= 0) {
            cur = cur.replace(tar, ' ');
        }
        cur = cur.trim();
        if (cur) {
            element.setAttribute('class', cur);
        } else {
            element.removeAttribute('class');
        }
    }
}

/**
 * If specified elememnt has classname or not.
 * @param {Element} element dom element.
 * @param {string} clazz class name.
 * @return {boolean} has classname or not.
 * Has class with compatibility for SVG since classList is not supported on SVG elements in IE
 */
export function hasClass(element, clazz) {
    if (!clazz || !(clazz = clazz.trim())) {
        return;
    }
    return ` ${element.getAttribute('class') || ''} `.indexOf(` ${clazz} `) > -1;
}

export function elementInViewport(el) {
    let top = el.offsetTop;
    let left = el.offsetLeft;
    const width = el.offsetWidth;
    const height = el.offsetHeight;

    while (el.offsetParent) {
        el = el.offsetParent;
        top += el.offsetTop;
        left += el.offsetLeft;
    }

    return (
        top >= window.pageYOffset &&
        left >= window.pageXOffset &&
        top + height <= window.pageYOffset + window.innerHeight &&
        left + width <= window.pageXOffset + window.innerWidth
    );
}

export function elementPartInViewport(el) {
    let top = el.offsetTop;
    let left = el.offsetLeft;
    const width = el.offsetWidth;
    const height = el.offsetHeight;

    while (el.offsetParent) {
        el = el.offsetParent;
        top += el.offsetTop;
        left += el.offsetLeft;
    }

    return (
        top < window.pageYOffset + window.innerHeight &&
        left < window.pageXOffset + window.innerWidth &&
        top + height > window.pageYOffset &&
        left + width > window.pageXOffset
    );
}

/* istanbul ignore next */
export const on = (function() {
    if (document.addEventListener) {
        return function(element, event, handler) {
            if (element && event && handler) {
                element.addEventListener(event, handler, false);
            }
        };
    }
    return function(element, event, handler) {
        if (element && event && handler) {
            element.attachEvent(`on${event}`, handler);
        }
    };
})();

/* istanbul ignore next */
export const off = (function() {
    if (document.removeEventListener) {
        return function(element, event, handler) {
            if (element && event) {
                element.removeEventListener(event, handler, false);
            }
        };
    }
    return function(element, event, handler) {
        if (element && event) {
            element.detachEvent(`on${event}`, handler);
        }
    };
})();

/**
 * 获取指定element元素的window对象
 * @param {Element} element element元素
 * @return {Window} window.
 */
export function getWindow(element) {
    return (element.ownerDocument && element.ownerDocument.defaultView) || null;
}

/**
 * 在节点之后插入新节点
 * @param {Element} node 原节点
 * @param {Element} newNode 新节点
 * @return {Element} 新插入的节点
 */
export function insertAfter(node, newNode) {
    return node.nextSibling
        ? node.parentNode.insertBefore(newNode, node.nextSibling)
        : node.parentNode.appendChild(newNode);
}

/**
 * 判断指定element元素是否有滚动条
 * @param {Element} element 指定的元素
 * @param {String} direction 滚动条方向，默认为垂直方向
 * @return {Boolean} 是否有滚动条
 */
export function hasScrollbar(element, direction = 'vertical') {
    if (direction === 'vertical') {
        return element.scrollHeight > element.clientHeight;
    }
    return element.scrollWidth > element.clientWidth;
}
